// src/components/landing/Pricing.tsx
// Landing page pricing sectie - Hybride Prijsmodel
// Modern Glassmorphism Design

import React, { useState } from 'react';
import ExamGrid from './ExamGrid';
import { trackPricingClick } from '../../services/analytics';

interface PricingProps {
  onNavigate: (path: string) => void;
}

type PricingTab = 'monthly' | 'yearly';

const Pricing: React.FC<PricingProps> = ({ onNavigate }) => {
  const [billingPeriod, setBillingPeriod] = useState<PricingTab>('monthly');

  const yearlyDiscount = Math.round((1 - (149.99 / (19.99 * 12))) * 100);

  return (
    <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50" />
      
      {/* Decorative blobs */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse" />
      <div className="absolute bottom-20 right-10 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse" />
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-green-400 to-emerald-500 text-white shadow-lg mb-4">
            💰 Transparante Prijzen
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Investeer in je{' '}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              WFT succes
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Kies het plan dat bij jou past. Start gratis of ga direct Pro.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex items-center p-1.5 rounded-2xl bg-white/60 backdrop-blur-sm shadow-lg border border-white/50">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                billingPeriod === 'monthly'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Maandelijks
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 flex items-center gap-2 ${
                billingPeriod === 'yearly'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Jaarlijks
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                billingPeriod === 'yearly' 
                  ? 'bg-white/20 text-white' 
                  : 'bg-green-100 text-green-700'
              }`}>
                -{yearlyDiscount}%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          
          {/* FREE Plan */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-gray-200 to-gray-300 rounded-3xl blur-xl opacity-50 group-hover:opacity-70 transition-opacity" />
            <div className="relative bg-white/70 backdrop-blur-xl rounded-3xl p-8 border border-white/50 shadow-xl h-full flex flex-col">
              {/* Header */}
              <div className="mb-6">
                <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                  <span className="text-2xl">🆓</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Gratis</h3>
                <p className="text-gray-500 mt-1">Perfect om te beginnen</p>
              </div>

              {/* Price */}
              <div className="mb-6">
                <div className="flex items-baseline">
                  <span className="text-5xl font-bold text-gray-900">€0</span>
                  <span className="text-gray-500 ml-2">/maand</span>
                </div>
              </div>

              {/* CTA */}
 <button
  onClick={() => {
    trackPricingClick({ plan: 'free', buttonLocation: 'pricing_section' });
    onNavigate('/register?plan=free');
  }}
  className="w-full py-3.5 px-6 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-all duration-300 mb-8"
>
  Start Gratis
</button>

              {/* Features */}
              <div className="space-y-4 flex-1">
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Inbegrepen:</p>
                
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-gray-600"><strong>5 AI vragen</strong> per dag</span>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-gray-600"><strong>1 proefexamen preview</strong> (10 vragen)</span>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-gray-600">Basis voortgang tracking</span>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-gray-400">Geen volledige proefexamens</span>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-gray-400">Geen samenvattingen</span>
                </div>
              </div>
            </div>
          </div>

          {/* PRO Plan - HIGHLIGHTED */}
          <div className="relative group md:-mt-4 md:mb-4">
            {/* Popular badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-20">
              <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-bold bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-lg">
                ⭐ POPULAIR
              </span>
            </div>
            
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-3xl blur-xl opacity-50 group-hover:opacity-70 transition-opacity" />
            <div className="relative bg-gradient-to-br from-blue-600 via-purple-600 to-purple-700 rounded-3xl p-8 border border-white/20 shadow-2xl h-full flex flex-col">
              {/* Header */}
              <div className="mb-6">
                <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center mb-4">
                  <span className="text-2xl">👑</span>
                </div>
                <h3 className="text-2xl font-bold text-white">Pro</h3>
                <p className="text-blue-100 mt-1">Alles wat je nodig hebt</p>
              </div>

              {/* Price */}
              <div className="mb-6">
                <div className="flex items-baseline">
                  <span className="text-5xl font-bold text-white">
                    €{billingPeriod === 'monthly' ? '19,99' : '149,99'}
                  </span>
                  <span className="text-blue-200 ml-2">
                    /{billingPeriod === 'monthly' ? 'maand' : 'jaar'}
                  </span>
                </div>
                {billingPeriod === 'yearly' && (
                  <p className="text-green-300 text-sm mt-1">
                      💰 Bespaar €{((19.99 * 12) - 149.99).toFixed(2)} per jaar
                  </p>
                )}
              </div>

              {/* CTA */}
  <button
  onClick={() => {
    trackPricingClick({ plan: 'pro', billing: billingPeriod, buttonLocation: 'pricing_section' });
    onNavigate(`/register?plan=pro&billing=${billingPeriod}`);
  }}
  className="w-full py-3.5 px-6 rounded-xl font-semibold text-purple-700 bg-white hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 mb-8"
>
  Start met Pro
</button>

              {/* Features */}
              <div className="space-y-4 flex-1">
                <p className="text-sm font-semibold text-blue-200 uppercase tracking-wider">Alles in Free, plus:</p>
                
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-green-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-white"><strong>Onbeperkt</strong> proefexamens</span>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-green-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-white"><strong>Alle 9 samenvattingen</strong></span>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-green-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-white"><strong>100 AI vragen</strong> per maand</span>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-green-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-white">Uitgebreide <strong>voortgang analytics</strong></span>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-green-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-white"><strong>Fout tracking</strong> & herkansing tips</span>
                </div>

                {billingPeriod === 'yearly' && (
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-amber-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs">🎁</span>
                    </div>
                    <span className="text-amber-200"><strong>+200 bonus</strong> AI vragen</span>
                  </div>
                )}
              </div>

              {/* Trust badges */}
              <div className="mt-6 pt-6 border-t border-white/20">
                <p className="text-blue-200 text-sm text-center">
                  🔒 Veilig betalen met iDEAL of creditcard
                </p>
              </div>
            </div>
          </div>

          {/* À LA CARTE */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-200 to-teal-300 rounded-3xl blur-xl opacity-50 group-hover:opacity-70 transition-opacity" />
            <div className="relative bg-white/70 backdrop-blur-xl rounded-3xl p-8 border border-white/50 shadow-xl h-full flex flex-col">
              {/* Header */}
              <div className="mb-6">
                <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center mb-4">
                  <span className="text-2xl">🛒</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">À la carte</h3>
                <p className="text-gray-500 mt-1">Koop wat je nodig hebt</p>
              </div>

              {/* Price indicator */}
              <div className="mb-6">
                <div className="flex items-baseline">
                  <span className="text-3xl font-bold text-gray-900">Vanaf</span>
                  <span className="text-4xl font-bold text-emerald-600 ml-2">€2,99</span>
                </div>
                <p className="text-gray-500 text-sm mt-1">Geen abonnement nodig</p>
              </div>

              {/* CTA */}
   <button
  onClick={() => {
    trackPricingClick({ plan: 'products', buttonLocation: 'pricing_section' });
    onNavigate('/register?plan=products');
  }}
  className="w-full py-3.5 px-6 rounded-xl font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 transition-all duration-300 shadow-md hover:shadow-lg mb-8"
>
  Bekijk Producten
</button>

              {/* Products */}
              <div className="space-y-4 flex-1">
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Losse producten:</p>
                
                <div className="bg-white/50 rounded-xl p-4 border border-gray-100">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      {/* <span className="text-xl">📚</span> */}
                      <span className="text-gray-700 font-medium">Samenvatting</span>
                    </div>
                    <span className="text-emerald-600 font-bold">€4,99</span>
                  </div>
                  <p className="text-gray-400 text-sm mt-1 ml-9">Per module (9 beschikbaar)</p>
                </div>

                <div className="bg-white/50 rounded-xl p-4 border border-gray-100">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      {/* <span className="text-xl">🎯</span> */}
                      <span className="text-gray-700 font-medium">Proefexamen</span>
                    </div>
                    <span className="text-emerald-600 font-bold">€4,99</span>
                  </div>
                  <p className="text-gray-400 text-sm mt-1 ml-9">Examensimulatie obv officiële toetstermen</p>
                </div>

                <div className="bg-white/50 rounded-xl p-4 border border-gray-100">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      {/* <span className="text-xl">🤖</span> */}
                      <span className="text-gray-700 font-medium">AI Vragen</span>
                    </div>
                    <span className="text-emerald-600 font-bold">€2,99+</span>
                  </div>
                  <p className="text-gray-400 text-sm mt-1 ml-9">50 of 150 vragen packs</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Comparison callout */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/50">
            <div className="flex items-center gap-2 text-gray-700">
              <span className="text-2xl">💡</span>
              <span className="font-medium">Tip:</span>
            </div>
            <p className="text-gray-600">
              Bij <strong>2+ modules</strong> is Pro voordeliger dan losse aankopen!
            </p>
            <div className="hidden sm:block w-px h-8 bg-gray-300" />
            <p className="text-gray-500 text-sm">
              Pro maandelijks = €19,99 | 2 samenvattingen + 2 exams = €19,96
            </p>
          </div>
        </div>

         {/* Modules overview - Nu met klikbare links naar SEO pagina's */}
        <div className="mt-16">
          <ExamGrid 
            showTitle={true}
            title="📖 Beschikbare WFT Examens"
            maxColumns={5}
            showToetstermen={true}
            variant="default"
          />
        </div>

        {/* FAQ Link */}
        <div className="mt-12 text-center">
          <p className="text-gray-600">
            <strong>Nog vragen?</strong> Bekijk onze{' '}
            <a href="#faq" className="text-blue-600 hover:underline font-medium">veelgestelde vragen</a>
            {' '}of{' '}
            <a href="mailto:support@wftstudybuddy.nl" className="text-blue-600 hover:underline font-medium">neem contact op</a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default Pricing;